import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Send, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { StatusPill, EmptyState, TableWrap, th, td, fmtDate } from "@/components/dashboard/ui";

export default function LabelRoster({ onChange }) {
  const [roster, setRoster] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", display_name: "", message: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, i] = await Promise.all([api.get("/label/roster"), api.get("/label/invitations")]);
      setRoster(r.data);
      setInvites(i.data.filter((x) => x.role === "artist"));
    } catch (e) {
      toast.error("Failed to load roster");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Enter an email address");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/label/invitations", { ...form, kind: "artist", app_url: window.location.origin });
      toast.success(data.email_sent ? `Invitation emailed to ${form.email}` : `Invitation created for ${form.email}`);
      setForm({ email: "", display_name: "", message: "" });
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to invite"));
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/label/invitations/${id}`);
      toast.success("Invitation revoked");
      load();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to revoke");
    }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";

  return (
    <div className="space-y-8" data-testid="label-roster">
      <form onSubmit={send} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="label-invite-artist-form">
        <h3 className="font-display text-lg text-white mb-4">Invite an artist to your label</h3>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <input data-testid="label-artist-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="artist@email.com" type="email" className={`md:col-span-5 ${inputCls}`} />
          <input data-testid="label-artist-name" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} placeholder="Artist name (optional)" className={`md:col-span-4 ${inputCls}`} />
          <button data-testid="label-artist-submit" type="submit" disabled={sending} className="md:col-span-3 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black font-medium px-5 py-3 hover:bg-slate-200 disabled:opacity-60 transition-all">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <><Send size={16} /> Invite</>}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : (
        <>
          <div>
            <h3 className="font-display text-lg text-white mb-4">Roster</h3>
            {roster.length === 0 ? (
              <EmptyState title="No artists yet" desc="Invite your first artist above." />
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <th className={th}>Artist</th>
                    <th className={th}>Releases</th>
                    <th className={th}>Status</th>
                    <th className={th}>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {roster.map((a) => (
                    <tr key={a.user_id} data-testid={`roster-row-${a.user_id}`} className="hover:bg-white/[0.02]">
                      <td className={td}>
                        <div className="text-white">{a.display_name || a.name}</div>
                        <div className="text-xs text-slate-500">{a.email}</div>
                      </td>
                      <td className={td}>{a.release_count}</td>
                      <td className={td}><StatusPill status={a.status} /></td>
                      <td className={td}>{fmtDate(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </div>

          {invites.length > 0 && (
            <div>
              <h3 className="font-display text-lg text-white mb-4">Pending artist invites</h3>
              <TableWrap>
                <thead>
                  <tr>
                    <th className={th}>Email</th>
                    <th className={th}>Name</th>
                    <th className={th}>Status</th>
                    <th className={th}>Sent</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => (
                    <tr key={inv.invitation_id} data-testid={`label-invite-row-${inv.invitation_id}`} className="hover:bg-white/[0.02]">
                      <td className={td + " text-white"}>{inv.email}</td>
                      <td className={td}>{inv.display_name}</td>
                      <td className={td}><StatusPill status={inv.status} /></td>
                      <td className={td}>{fmtDate(inv.created_at)}</td>
                      <td className={td + " text-right"}>
                        {inv.status === "pending" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`label-invite-actions-${inv.invitation_id}`} className="p-2 rounded-lg hover:bg-white/10 text-slate-400"><MoreHorizontal size={18} /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#0b1220] border-white/10 text-white">
                              <DropdownMenuItem onClick={() => revoke(inv.invitation_id)} className="text-red-400 focus:bg-white/10 focus:text-red-400 cursor-pointer" data-testid={`label-invite-revoke-${inv.invitation_id}`}>
                                Revoke invitation
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </div>
          )}
        </>
      )}
    </div>
  );
}
